import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { remoteApi } from '../../config/apiClient';

/**
 * TV uygulaması (medya / sipariş ekranı) güncellemesi: gts_remote_api'deki şirket uçları.
 * Çevrimiçi bilgisi remote API'nin canlı bağlantısından gelir; gönderilen sürüm her zaman
 * GTS Remote'a yüklenen son sürümdür.
 */

export type TvInstallStatus =
  | 'pending' | 'sent' | 'downloading' | 'ready' | 'installing' | 'completed' | 'failed' | 'cancelled';

export interface TvInstall {
  id: number;
  status: TvInstallStatus;
  version?: string | null;
  progress: number;
  error?: string | null;
  requestedAt: string;
  requestedBy?: string | null;
}

export interface TvScreenStatus {
  screenCode: string;
  deviceCode?: string | null;
  online: boolean;
  appVersion?: string | null;
  lastSeenAt?: string | null;
  deviceModel?: string | null;
  install?: TvInstall | null;
}

export interface TvLatest {
  id: number;
  version: string;
  uploadedAt: string;
}

export interface TvSendResult {
  planned: number;
  sentNow: number;
  alreadyCurrent: number;
  noDevice: number;
  version?: string | null;
}

/** TV uygulamasını kullanan ekran tipleri. */
export const TV_APP_SCREEN_TYPES = ['media', 'orders'];

const errorMessage = (e: any, fallback: string): string =>
  e?.response?.data?.message || e?.response?.data?.Message || e?.message || fallback;

// companyId yalnız süper admin için anlamlı; diğer kullanıcılarda sunucu oturumdaki şirketi kullanır
export const tvAppService = {
  status: async (screenCodes: string[], companyId?: string | null) => {
    const res = await remoteApi.post('/api/tv-management/company/status', { screenCodes, companyId });
    return { latest: (res.data?.latest ?? null) as TvLatest | null, screens: (res.data?.screens ?? []) as TvScreenStatus[] };
  },
  installLatest: async (screenCodes: string[], companyId?: string | null) => {
    const res = await remoteApi.post('/api/tv-management/company/install-latest', { screenCodes, companyId });
    return res.data?.data as TvSendResult;
  },
  queryVersion: async (screenCodes: string[], companyId?: string | null) => {
    const res = await remoteApi.post('/api/tv-management/company/query-version', { screenCodes, companyId });
    return Number(res.data?.asked ?? 0);
  },
  errorMessage,
};

const POLL_MS = 30_000;

/** Ekranların TV durumu: açılışta, ekran listesi değişince ve 30 sn'de bir. */
export function useTvAppStatus(screenCodes: string[], companyId: string | null) {
  const enabled = !!companyId;
  const [byCode, setByCode] = useState<Record<string, TvScreenStatus>>({});
  const [latest, setLatest] = useState<TvLatest | null>(null);
  const [loadError, setLoadError] = useState(false);
  const codesKey = screenCodes.slice().sort().join(',');
  const codesRef = useRef(screenCodes);
  codesRef.current = screenCodes;

  const refresh = useCallback(async () => {
    const codes = codesRef.current;
    if (!enabled || codes.length === 0) {
      setByCode({});
      return;
    }
    try {
      const r = await tvAppService.status(codes, companyId);
      const map: Record<string, TvScreenStatus> = {};
      for (const s of r.screens) map[s.screenCode] = s;
      setByCode(map);
      setLatest(r.latest);
      setLoadError(false);
    } catch (e) {
      console.warn('[TV] Durum okunamadı:', e);
      setLoadError(true);
    }
  }, [enabled, companyId]);

  useEffect(() => {
    void refresh();
    if (!enabled) return undefined;
    const timer = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey, enabled, refresh]);

  return { byCode, latest, loadError, refresh };
}

type Tr = (trText: string, enText: string) => string;

/** TV güncel değil ve gönderilebilir mi (cihaz bağlanmış, son sürüm farklı, açık kurulum yok). */
export function tvNeedsUpdate(s: TvScreenStatus | undefined, latest: TvLatest | null): boolean {
  if (!s?.deviceCode || !latest) return false;
  if (s.appVersion === latest.version) return false;
  const st = s.install?.status;
  const openForLatest = s.install?.version === latest.version
    && (st === 'pending' || st === 'sent' || st === 'downloading' || st === 'ready' || st === 'installing');
  return !openForLatest;
}

/** Kurulum satırı: TV'nin bildirdiği aşama. */
function installLine(s: TvScreenStatus, latest: TvLatest | null, tr: Tr): { text: string; color: string; progress?: number } | null {
  const i = s.install;
  if (i && i.version && i.version !== s.appVersion) {
    switch (i.status) {
      case 'pending':
        return { text: tr(`${i.version} TV açılınca gönderilecek`, `${i.version} will be sent when the TV comes online`), color: 'text.secondary' };
      case 'sent':
        return { text: tr(`${i.version} gönderildi`, `${i.version} sent`), color: 'info.main' };
      case 'downloading':
        return { text: tr(`${i.version} indiriliyor`, `Downloading ${i.version}`), color: 'info.main', progress: i.progress > 0 ? i.progress : undefined };
      case 'ready':
        return { text: tr('Kurulum bekliyor: TV\'de "Yükle"ye basın', 'Waiting: press "Install" on the TV'), color: 'warning.main' };
      case 'installing':
        return { text: tr(`${i.version} kuruluyor`, `Installing ${i.version}`), color: 'info.main' };
      case 'failed':
        return { text: tr(`Güncelleme başarısız${i.error ? `: ${i.error}` : ''}`, `Update failed${i.error ? `: ${i.error}` : ''}`), color: 'error.main' };
      default:
        break;
    }
  }
  if (latest && s.appVersion && s.appVersion === latest.version) {
    return { text: tr('Güncel', 'Up to date'), color: 'success.main' };
  }
  if (latest && s.appVersion && s.appVersion !== latest.version) {
    return { text: tr(`${latest.version} var`, `${latest.version} available`), color: 'warning.main' };
  }
  return null;
}

/** Cihaz Yönetimi tablosundaki "TV Uygulaması" hücresi. */
export const TvAppStatusCell: React.FC<{ status?: TvScreenStatus; latest: TvLatest | null; tr: Tr; dense?: boolean }> = ({ status, latest, tr, dense }) => {
  if (!status?.deviceCode) {
    return <Typography variant="caption" color="text.secondary">{tr('TV bağlanmadı', 'No TV connected')}</Typography>;
  }
  const line = installLine(status, latest, tr);
  return (
    <Box sx={{ display: 'grid', gap: 0.25, minWidth: dense ? 0 : 150 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          variant="outlined"
          color={status.online ? 'success' : 'error'}
          label={status.online ? tr('Çevrimiçi', 'Online') : tr('Çevrimdışı', 'Offline')}
          icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'currentColor', ml: '8px !important' }} />}
        />
        {status.appVersion && (
          <Typography variant="body2" fontFamily="monospace">{status.appVersion}</Typography>
        )}
      </Box>
      {line && (
        <Typography variant="caption" sx={{ color: line.color }}>{line.text}</Typography>
      )}
      {line?.progress !== undefined && (
        <LinearProgress variant="determinate" value={line.progress} sx={{ height: 4, borderRadius: 2 }} />
      )}
    </Box>
  );
};

export interface TvUpdateTarget {
  screenCode: string;
  screenName: string;
  status: TvScreenStatus;
}

/** Gönderim onayı: hangi TV'lere hangi sürüm gidecek. */
export const TvUpdateConfirmDialog: React.FC<{
  open: boolean;
  latest: TvLatest | null;
  targets: TvUpdateTarget[];
  sending: boolean;
  tr: Tr;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ open, latest, targets, sending, tr, onClose, onConfirm }) => {
  const offline = targets.filter(t => !t.status.online).length;
  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>
        {tr(`${targets.length} TV'ye ${latest?.version ?? ''} gönderilsin mi?`, `Send ${latest?.version ?? ''} to ${targets.length} TV(s)?`)}
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2">
          {targets.map(t => `${t.screenName} (${t.status.appVersion || '?'}${t.status.online ? '' : tr(', çevrimdışı', ', offline')})`).join(' · ')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tr(
            'TV\'ler dosyayı indirir. Kurulum için TV başında birinin "Yükle"ye basması gerekir.',
            'The TVs download the file. Someone at the TV must press "Install" to finish.',
          )}
          {offline > 0 && ' ' + tr('Çevrimdışı TV açıldığında alır.', 'Offline TVs receive it when they come online.')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>{tr('Vazgeç', 'Cancel')}</Button>
        <Button variant="contained" onClick={onConfirm} disabled={sending || targets.length === 0}>
          {sending ? tr('Gönderiliyor…', 'Sending…') : tr('Gönder', 'Send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
